/**
 * useIdentityRequestOtp — Request OTP via GraphQL federation mutation
 *
 * Calls initiateIdentityOtpChallenge mutation through the proxy:
 *   Client → /api/v2/graphql → API Gateway → Auth Service (Apollo Federation)
 *
 * Uses codegen-generated hook with identity Apollo client (gateway endpoint).
 * Stores challenge session in identityAuthStore$ (in-memory, not persistent).
 */
import { useCallback } from 'react';
import { identityAuthStore$ } from '../../stores/identity-auth.store';
import { useInitiateIdentityOtpChallengeMutation } from '../../api/identity';
import { getIdentityClient } from '../../api/identity/identity-client';

interface IdentityRequestOtpParams {
    phoneNumber: string;
    notificationChannel?: 'SMS' | 'ZALO';
}

interface IdentityRequestOtpResult {
    success: boolean;
    session?: string;
    isNewUser?: boolean;
    message?: string;
    errorCode?: string;
}

export const useIdentityRequestOtp = () => {
    const [initiateOtpChallenge] = useInitiateIdentityOtpChallengeMutation({
        client: getIdentityClient(),
    });

    return useCallback(
        async (params: IdentityRequestOtpParams): Promise<IdentityRequestOtpResult> => {
            try {
                identityAuthStore$.isLoading.set(true);
                identityAuthStore$.error.set(undefined);
                identityAuthStore$.phoneNumber.set(params.phoneNumber);
                identityAuthStore$.notificationChannel.set(params.notificationChannel || 'SMS');

                const { data, errors } = await initiateOtpChallenge({
                    variables: {
                        phoneNumber: params.phoneNumber,
                        notificationChannel: params.notificationChannel || 'SMS',
                    },
                });

                if (errors?.length) {
                    const errorMessage = errors[0].message;
                    identityAuthStore$.error.set(errorMessage);
                    return { success: false, message: errorMessage, errorCode: 'GRAPHQL_ERROR' };
                }

                const result = data?.initiateIdentityOtpChallenge;

                if (result?.success && result.session) {
                    identityAuthStore$.challengeSession.set(result.session);
                    identityAuthStore$.authenticationStep.set('VerifyOtp');
                    identityAuthStore$.otpStartTime.set(Date.now());
                    identityAuthStore$.isNewUser.set(result.isNewUser ?? false);
                    return { success: true, session: result.session, isNewUser: result.isNewUser ?? false };
                }

                const message = result?.message || 'Failed to request OTP';
                identityAuthStore$.error.set(message);
                return { success: false, message, errorCode: result?.errorCode ?? undefined };
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Network error';
                identityAuthStore$.error.set(message);
                return { success: false, message, errorCode: 'NETWORK_ERROR' };
            } finally {
                identityAuthStore$.isLoading.set(false);
            }
        },
        [initiateOtpChallenge]
    );
};
